import { InsightTag } from '@ecosystem/newron-client-collections/build/shared/types';
import { SortKeys } from '@models/OpportunityReview';
import { getSortDirectionLabel, kpiDropdownOptions } from '@models/PerformanceAnalysisboard';
import { getCountryCodes } from '@utils/CountryUtils';
import { getBaseTimePeriodForMATAndYTD, getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';
import { capitalizeEachWord } from '@utils/StringUtils';
import {
  addCountryLoadedTagInPlace,
  addPgLoadedTagInPlace,
  getGeneralCollectionInsightTags,
  getIsUsingCollectionLiveData,
  getPeriodLabel,
} from '@utils/collectionUtils/CollectionUtils';
import { PerformanceAnalysisStackedBarChartInsight } from './Model';
import { PageName } from '@utils/pageConfig/Types';

export const getStackedBarChartInsightInfoTags = async (
  insight: PerformanceAnalysisStackedBarChartInsight,
): Promise<InsightTag[]> => {
  const {
    apiParams: {
      clientId,
      periodType,
      period,
      comparisonPeriod,
      countries,
      appliedFilters,
      kpi,
      productType,
      productGroup = [],
      pageName,
    },
    segmentByFeature,
    showPerformanceWithin,
    sortByStackedChart,
    selectedGrowthType,
    volume,
    collectionDataType,
    retailerClientName,
  } = insight;

  const baseTimePeriod = getBaseTimePeriodForMATAndYTD(period, periodType);
  const { periodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, periodType);
  const periodLabel = await getPeriodLabel({
    clientId,
    periodicity,
    baseTimePeriod,
    comparisonPeriod,
    countryIds: getCountryCodes(countries),
    productType,
    //when the period value is YTD/MAT, we pass in the collectionDataType as live
    //this is because we would need to display the Date Range for MAT and YTD
    collectionDataType: getIsUsingCollectionLiveData(period, collectionDataType) ? 'live' : collectionDataType,
    productGroups: productGroup,
  });
  const baseTags = getGeneralCollectionInsightTags({
    periodType: periodicity,
    comparisonPeriod:
      typeof comparisonPeriod === 'object' && comparisonPeriod !== null
        ? JSON.stringify(comparisonPeriod)
        : String(comparisonPeriod),
    selectedGrowthType: selectedGrowthType,
    appliedFilters: appliedFilters,
    retailerClientName: retailerClientName,
    period: periodLabel,
  });

  if (countries.length > 1) {
    const baseApiParams = {
      clientId,
      countries,
      productType,
      period,
      comparisonPeriod,
      productGroup,
      periodType,
      appliedFilters: [],
    };

    await addCountryLoadedTagInPlace(baseTags, baseApiParams, collectionDataType);
  } else {
    const baseApiParams = {
      ...insight.apiParams,
      productGroup,
    };

    await addPgLoadedTagInPlace(baseTags, baseApiParams, collectionDataType);
  }

  const kpiValue = kpiDropdownOptions(volume).filter((kpiOption) => {
    return kpiOption.value === kpi;
  })[0].label;
  let otherStackedChartTags = [
    {
      key: 'ShowPerformanceWithin',
      value: capitalizeEachWord(showPerformanceWithin.label.toString()),
    },
    {
      key: 'SegmentedBy',
      value: capitalizeEachWord(segmentByFeature.label.toString()),
    },
    {
      key: 'SortBy',
      value: `${sortByStackedChart.label} ${getSortDirectionLabel(
        sortByStackedChart.sortDirection,
        SortKeys[sortByStackedChart.sortKey as keyof typeof SortKeys],
      )}`,
    },
    {
      key: 'KPI',
      value: `${kpiValue}`,
    },
  ];

  if (pageName === PageName.EXPLORE) {
    otherStackedChartTags = [
      {
        key: 'Show',
        value: capitalizeEachWord(showPerformanceWithin.label.toString()),
      },
      {
        key: 'SegmentedBy',
        value: capitalizeEachWord(segmentByFeature.label.toString()),
      },
      {
        key: 'SortBy',
        value: `${sortByStackedChart.label} ${getSortDirectionLabel(
          sortByStackedChart.sortDirection,
          SortKeys[sortByStackedChart.sortKey as keyof typeof SortKeys],
        )}`,
      },
      {
        key: 'KPI',
        value: `${kpiValue}`,
      },
    ];
  }

  return [...baseTags, ...otherStackedChartTags];
};
